import React from 'react';
import useHttp from '../../../hooks/http.hook';
import './CustomerStatsList.css';

async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    // fall through
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (_) {
    return false;
  }
}

function SummaryIcon({ tone }) {
  if (tone === 'teal') {
    return (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6h15l-1.5 8.5H8L6 3H3" stroke="#0E9F9F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="20" r="1.6" stroke="#0E9F9F" strokeWidth="2" />
        <circle cx="17" cy="20" r="1.6" stroke="#0E9F9F" strokeWidth="2" />
      </svg>
    );
  }
  if (tone === 'purple') {
    return (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 17l6-6 4 4 8-8" stroke="#7A5AF8" strokeWidth="2" strokeLinecap="round" />
        <path d="M15 7h6v6" stroke="#7A5AF8" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="4" stroke="#2E6BF0" strokeWidth="2" />
      <path d="M4 21c1.5-4 5-5.5 8-5.5s6.5 1.5 8 5.5" stroke="#2E6BF0" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CustomerStatsList({ branchKey, active = false }) {
  const { requestWithMeta } = useHttp();
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [totals, setTotals] = React.useState({ customers: 0, orders: 0 });
  const [fetchError, setFetchError] = React.useState(false);
  const [retryTick, setRetryTick] = React.useState(0);
  const [toast, setToast] = React.useState('');
  const toastTimerRef = React.useRef(null);

  const branch = branchKey || 'store';

  React.useEffect(() => {
    if (!active) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const res = await requestWithMeta(
          `/operator/stats/customers/branch/${encodeURIComponent(branch)}`,
          'GET'
        );
        if (cancelled) return;
        if (!res?.ok) {
          setItems([]);
          setTotals({ customers: 0, orders: 0 });
          setFetchError(true);
          return;
        }
        const raw = res?.data?.stats;
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list.map((row) => {
          const orderCount = Number(row?.orderCount ?? row?.order_count ?? 0);
          return {
            customerName: String(row?.customerName ?? row?.customer_name ?? '').trim() || '—',
            customerPhone: String(row?.customerPhone ?? row?.customer_phone ?? '').trim() || '—',
            orderCount: Number.isFinite(orderCount) ? orderCount : 0,
          };
        });
        const rawTotals = res?.data?.totals ?? {};
        const customers = Number(rawTotals.customers ?? normalized.length);
        const orders = Number(
          rawTotals.orders ?? normalized.reduce((sum, row) => sum + row.orderCount, 0)
        );
        setItems(normalized);
        setTotals({
          customers: Number.isFinite(customers) ? customers : 0,
          orders: Number.isFinite(orders) ? orders : 0,
        });
        setFetchError(false);
      } catch (_) {
        if (!cancelled) {
          setItems([]);
          setTotals({ customers: 0, orders: 0 });
          setFetchError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, branch, requestWithMeta, retryTick]);

  const nf = React.useMemo(() => new Intl.NumberFormat('ru-RU'), []);
  const nfAvg = React.useMemo(
    () => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    []
  );
  const onRetry = () => setRetryTick((t) => t + 1);
  const avgOrders = totals.customers > 0 ? totals.orders / totals.customers : null;
  const topCount = items.reduce((max, row) => (row.orderCount > max ? row.orderCount : max), 0);

  const showToast = (message) => {
    setToast(message);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      toastTimerRef.current = null;
      setToast('');
    }, 1800);
  };

  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const onCopyPhone = async (phone) => {
    const ok = await copyToClipboard(phone);
    showToast(ok ? 'Телефон скопирован' : 'Не удалось скопировать');
  };

  if (!active) return null;

  return (
    <section
      id="analytics-section-customers"
      className="customer-stats"
      aria-label="Клиенты"
    >
      {loading ? (
        <div className="customer-stats-loading" aria-busy="true">
          <div className="customer-stats-totals customer-stats-totals--skeleton">
            <div className="customer-stats-skel customer-stats-skel--total" />
            <div className="customer-stats-skel customer-stats-skel--total" />
            <div className="customer-stats-skel customer-stats-skel--total" />
          </div>
          <div className="customer-stats-tableWrap">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="customer-stats-skel customer-stats-skel--row" />
            ))}
          </div>
        </div>
      ) : fetchError ? (
        <div className="customer-stats-fallback">
          <p className="customer-stats-fallbackText">Не удалось загрузить данные.</p>
          <button type="button" className="customer-stats-retry" onClick={onRetry}>
            Повторить
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="customer-stats-empty">Нет клиентов.</p>
      ) : (
        <>
          <div className="customer-stats-totals" aria-label="Итоги">
            <div className="customer-stats-totalCard">
              <div className="customer-stats-ico customer-stats-ico--blue">
                <SummaryIcon tone="blue" />
              </div>
              <div>
                <div className="customer-stats-totalValue">{nf.format(totals.customers)}</div>
                <div className="customer-stats-totalLabel">Клиентов</div>
              </div>
            </div>
            <div className="customer-stats-totalCard">
              <div className="customer-stats-ico customer-stats-ico--teal">
                <SummaryIcon tone="teal" />
              </div>
              <div>
                <div className="customer-stats-totalValue">{nf.format(totals.orders)}</div>
                <div className="customer-stats-totalLabel">Заказов</div>
              </div>
            </div>
            <div className="customer-stats-totalCard">
              <div className="customer-stats-ico customer-stats-ico--purple">
                <SummaryIcon tone="purple" />
              </div>
              <div>
                <div className="customer-stats-totalValue">
                  {avgOrders == null ? '—' : nfAvg.format(avgOrders)}
                </div>
                <div className="customer-stats-totalLabel">Среднее заказов / клиента</div>
              </div>
            </div>
          </div>

          <div className="customer-stats-tableWrap">
            <table className="customer-stats-table">
              <thead>
                <tr>
                  <th scope="col">Клиент</th>
                  <th scope="col">Телефон</th>
                  <th scope="col" className="customer-stats-colCount">Заказы</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => {
                  const isTop = idx === 0 && row.orderCount > 0 && row.orderCount === topCount;
                  return (
                    <tr
                      key={`${row.customerPhone}:${idx}`}
                      className={isTop ? 'customer-stats-row--top' : undefined}
                    >
                      <td className="customer-stats-name">
                        <span className="customer-stats-lbl">Клиент</span>
                        {row.customerName}
                      </td>
                      <td className="customer-stats-phone">
                        <span className="customer-stats-lbl">Телефон</span>
                        <span>{row.customerPhone}</span>
                        {row.customerPhone !== '—' ? (
                          <button
                            type="button"
                            className="customer-stats-copy"
                            onClick={() => onCopyPhone(row.customerPhone)}
                          >
                            скопировать
                          </button>
                        ) : null}
                      </td>
                      <td className="customer-stats-count">
                        <span className="customer-stats-lbl">Заказы</span>
                        <span className={isTop ? 'customer-stats-badge customer-stats-badge--top' : 'customer-stats-badge'}>
                          {isTop ? '★ ' : ''}
                          {nf.format(row.orderCount)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className={toast ? 'customer-stats-toast customer-stats-toast--show' : 'customer-stats-toast'} role="status">
            {toast}
          </div>
        </>
      )}
    </section>
  );
}

export default CustomerStatsList;
